import { useCallback, useState } from 'react';

/**
 * Drop-in replacement for useState.
 *
 * For many state fields, we use three types of values to clarify if data has been
 * requested from the backend and what that value is:
 *   - Actual type (`T`): The state is truthy
 *   - `undefined`: The state has not been obtained from the backend
 *   - `null`: The state is falsy in the backend.
 * NOTE: Some values may not be nullable, so they can only be `undefined` or `T`.
 * As a result, the actual useState should allow all three of these types, but when
 * we update the value from the frontend, we never want to set to `undefined`.
 */
export function useMonotonicState<T>(
  initial?: T,
): [T | undefined, (updated: T) => void, (updated: T | undefined) => void] {
  const [value, unsafeSetValue] = useState<T | undefined>(initial);

  const safeSetter = useCallback(
    (updated: T) => {
      unsafeSetValue(updated);
    },
    [unsafeSetValue],
  );

  return [value, safeSetter, unsafeSetValue];
}

export function checkExists<T>(value: T): value is Exclude<T, undefined | null> {
  return value !== null && value !== undefined;
}
